import React, { useState, useMemo } from 'react'
import { useNotificationBar } from 'context/notificationContext'
import { useForm } from 'react-hook-form'
import { useAIPrompts, useCreateAIPrompt, useUpdateAIPrompt, useDeleteAIPrompt, useTogglePromptStatus, usePromptVersions } from 'hooks/ai-settings'
import { TextareaField } from 'components/styledFormElements/TextareaField'
import { PencilIcon, TrashIcon, CheckCircleIcon, XCircleIcon, SparklesIcon, ClockIcon } from '@heroicons/react/24/outline'
import { ConfirmModal } from 'components/confirmationPopup'
import { CustomTable } from 'components/customTable'
import { ColumnDef, RowSelectionState } from '@tanstack/react-table'
import { LoaderMain } from 'components/loader'
import moment from 'moment'

interface AIPrompt {
  id?: string
  prompt: string
  status: number // 1 = active, 0 = inactive
  is_delete?: number // 1 = deleted, 0 = not deleted
  createdAt?: string
  updatedAt?: string
  createdBy?: number
  updatedBy?: number
}

interface PromptVersion {
  id: string
  promptId: string
  promptText: string
  status: number
  is_delete: number
  versionNumber: number
  changeReason: string
  createdAt: string
  createdBy: number
}

interface PromptFormData {
  prompt: string
  status: number
}

const defaultPrompt = `You are generating semantic labels for a consumer video app.

These labels are used for search and personalization, not for summarizing steps or teaching methods.

Your task is to extract a small, bounded set of labels that describe what the content is fundamentally about at a level that would still be valid even if the examples or tactics changed.

Core abstraction rule (most important)

A valid label must pass this test:

If the same content were remade with different examples or techniques, this label would still apply.

If a label only makes sense because of a specific trick, framework, rule, or explanation, it is not a valid label.

What labels should represent

Each label should capture one of the following:

The main problem the user cares about

The primary outcome the user wants to achieve

The broader domain or life context

The evaluation or reward system being navigated

What labels must NOT be

Do NOT generate labels that are:

Specific tactics, methods, habits, or routines

Named techniques or internal constructs

Awkward or unnatural phrases users would not search

Language & phrasing rules

Labels must be simple, natural, and user-facing

Prefer commonly used words over formal language

Labels should usually be 1–3 words

De-duplication rule

If the same label text appears multiple times, keep only one

Structural labels take precedence over semantic and user labels

Output

Return only a clean list of labels.

Do not include explanations or formatting beyond the list.`

export function AISettings() {
  const notification = useNotificationBar()
  const { data: prompts, isLoading, refetch } = useAIPrompts({
    pageSize: 100,
    pageIndex: 0
  })
  const createPrompt = useCreateAIPrompt()
  const updatePrompt = useUpdateAIPrompt()
  const deletePrompt = useDeleteAIPrompt()
  const toggleStatus = useTogglePromptStatus()

  const { register, handleSubmit, reset, formState: { errors } } = useForm<PromptFormData>()
  const filterFormHook = useForm<any>({
    defaultValues: {
      pagination: { pageIndex: 0, pageSize: 10 },
      search: '',
      sort: null,
      fields: []
    }
  })

  const rowSelectionState = useState<RowSelectionState>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingPrompt, setEditingPrompt] = useState<AIPrompt | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [promptToDelete, setPromptToDelete] = useState<string | null>(null)
  const [showVersionHistory, setShowVersionHistory] = useState(false)
  const [selectedPromptForHistory, setSelectedPromptForHistory] = useState<AIPrompt | null>(null)

  const handleAddNew = () => {
    setEditingPrompt(null)
    reset({
      prompt: defaultPrompt,
      status: 1 // New prompts are active by default
    })
    setShowModal(true)
  }

  const handleEdit = (promptData: AIPrompt) => {
    setEditingPrompt(promptData)
    reset({
      prompt: promptData.prompt,
      status: promptData.status
    })
    setShowModal(true)
  }

  const handleDeleteClick = (promptId: string) => {
    setPromptToDelete(promptId)
    setDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!promptToDelete) return
    
    try {
      await deletePrompt.mutateAsync({ prompt_id: promptToDelete })
      notification.push({
        title: 'Success',
        type: 'success',
        message: 'Prompt deleted successfully'
      })
      refetch()
    } catch (error:any) {
      notification.push({
        title: 'Error',
        type: 'error',
        message: error?.response?.data?.message || 'Failed to delete prompt'
      })
    } finally {
      setDeleteConfirmOpen(false)
      setPromptToDelete(null)
    }
  }

  const onSubmit = async (data: PromptFormData) => {
    setIsSubmitting(true)
    try {
      if (editingPrompt) {
        await updatePrompt.mutateAsync({
          prompt_id: editingPrompt.id!,
          data: {
            prompt: data.prompt,
            status: data.status,
            is_delete: 0
          }
        })
        notification.push({
          title: 'Success',
          type: 'success',
          message: 'Prompt updated successfully'
        })
      } else {
        await createPrompt.mutateAsync({
          data: {
            prompt: data.prompt,
            status: data.status,
            is_delete: 0
          }
        })
        notification.push({
          title: 'Success',
          type: 'success',
          message: 'Prompt created successfully'
        })
      }

      refetch()
      setShowModal(false)
      reset()
    } catch (error) {
      notification.push({
        title: 'Error',
        type: 'error',
        message: `Failed to ${editingPrompt ? 'update' : 'create'} prompt`
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleToggleStatus = async (promptData: AIPrompt) => {
    try {
      const newStatus = promptData.status === 1 ? 0 : 1
      await toggleStatus.mutateAsync({
        prompt_id: promptData.id!,
        status: newStatus
      })
      notification.push({
        title: 'Success',
        type: 'success',
        message: `Prompt ${promptData.status === 1 ? 'deactivated' : 'activated'} successfully`
      })
      refetch()
    } catch (error: any) {
      notification.push({
        title: 'Error',
        type: 'error',
        message: error?.response?.data?.message || 'Failed to update prompt status'
      })
    }
  }

  const handleViewHistory = (prompt: AIPrompt) => {
    setSelectedPromptForHistory(prompt)
    setShowVersionHistory(true)
  }

  const columns = useMemo<ColumnDef<AIPrompt>[]>(
    () => [
      {
        accessorKey: 'prompt',
        header: 'Prompt',
        cell: ({ row }) => (
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              Prompt #{row.original.id?.substring(0, 8)}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-md">
              {row.original.prompt.substring(0, 100)}...
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <button
            onClick={() => handleToggleStatus(row.original)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              row.original.status === 1
                ? 'bg-blue-600'
                : 'bg-gray-300 dark:bg-gray-600'
            }`}
            role="switch"
            aria-checked={row.original.status === 1}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                row.original.status === 1 ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => handleViewHistory(row.original)}
              className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300"
              title="Version History"
            >
              <ClockIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => handleEdit(row.original)}
              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
              title="Edit"
            >
              <PencilIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => handleDeleteClick(row.original.id!)}
              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
              title="Delete"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        ),
      },
    ],
    []
  )

  const responseData = useMemo(() => {
    return {
      results: prompts || [],
      pages: 1,
      total: prompts?.length || 0,
      pagination: { pageSize: 10, pageIndex: 0 }
    }
  }, [prompts])

  if (isLoading) {
    return <LoaderMain />
  }

  return (
    <div className="w-full px-6 pt-6 bg-white dark:text-gray-200 dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-800 shadow-md">
      <aside className="flex flex-auto items-center gap-2 mb-4">
        <SparklesIcon className="h-6 w-6" />
        <h4 className="text-xl">AI Label Generation Prompts</h4>
      </aside>
      
      <div className="mb-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Manage prompts used for AI-powered label generation. Only one prompt can be active at a time.
        </p>
      </div>

      <CustomTable
        columns={columns}
        data={responseData.results}
        pages={responseData.pages}
        totalRecords={responseData.total}
        rowSelectionState={rowSelectionState}
        isLoading={isLoading}
        formHook={filterFormHook}
        title="AI Prompts"
        onAddAction={handleAddNew}
        addButtonText="Add New Prompt"
        onRefresh={refetch}
        searchVisible={false}
      />

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {editingPrompt ? 'Edit Prompt' : 'Add New Prompt'}
              </h3>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
              <div>
                <TextareaField
                  label="Prompt Text"
                  name="prompt"
                  register={register('prompt', { required: true })}
                  errors={errors as any}
                  rows={15}
                  placeholder="Enter the AI prompt text..."
                  required
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false)
                    reset()
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : editingPrompt ? 'Update Prompt' : 'Create Prompt'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteConfirmOpen}
        title="Delete Prompt"
        message="Are you sure you want to delete this prompt? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onCancel={() => {
          setDeleteConfirmOpen(false)
          setPromptToDelete(null)
        }}
        onConfirm={handleDeleteConfirm}
      />

      {/* Version History Modal */}
      {showVersionHistory && selectedPromptForHistory && (
        <VersionHistoryModal
          prompt={selectedPromptForHistory}
          onClose={() => {
            setShowVersionHistory(false)
            setSelectedPromptForHistory(null)
          }}
        />
      )}
    </div>
  )
}

// Version History Modal Component
function VersionHistoryModal({ 
  prompt, 
  onClose
}: { 
  prompt: AIPrompt
  onClose: () => void
}) {
  const { data: versions, isLoading } = usePromptVersions(prompt.id!, {
    pageSize: 50,
    pageIndex: 0
  })

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Version History
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Prompt #{prompt.id?.substring(0, 8)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <XCircleIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <LoaderMain />
            </div>
          ) : versions && versions.length > 0 ? (
            <div className="space-y-4">
              {versions.map((version: PromptVersion) => (
                <div
                  key={version.id}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-slate-700"
                >
                  <div className="mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Version {version.versionNumber}
                      </span>
                      {version.status === 1 && (
                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          Active
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {moment(version.createdAt).format('MMM DD, YYYY HH:mm')} • {version.changeReason}
                    </div>
                  </div>
                  <div className="bg-gray-100 dark:bg-slate-900 rounded p-3 max-h-40 overflow-y-auto">
                    <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono">
                      {version.promptText}
                    </pre>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No version history available
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
